/**
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/dashboard/sales-report.tsx — its
 * `WidgetCard` + recharts `ComposedChart` of stacked monthly bars with a legend in the description,
 * fed by `GET /api/billing/reports/sales-trend?bucket=month` (invoicing-admin.md §9) instead of the
 * template's revenue/expense constants: taxable value and GST per calendar month, zero-filled by the
 * API.
 *
 * Changed from the template on purpose (dataviz skill): gridlines are solid hairlines, not dashed;
 * the decorative bump `Area` repeating the bottom series is gone; the two stacked series are two
 * steps of one hue (they are parts of the same invoice, not rival categories), separated by a 2px
 * surface gap rather than a border. Every figure shown — both headlines, every tooltip line — is the
 * API's own `totals` or bucket value; nothing is added up here.
 */
import { Bar, CartesianGrid, ComposedChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { PiChartBarDuotone as ChartBar } from "react-icons/pi";
import { WidgetCard } from "@steelyard/theme";
import { useMedia } from "@core/hooks/use-media";
import { Badge } from "rizzui";
import { TileBoundary } from "./TileBoundary";
import { EmptyTile, UnreadableWidgetCard } from "./TrendStatCard";
import { STEEL, STEEL_LIGHT, compactMoney } from "./palette";
import type { SalesTrendReport } from "../../lib/services/invoicing";
import { money } from "../../lib/services/ordering";
import { formatMoney } from "../../lib/format";

const MONTH = new Intl.DateTimeFormat("en-GB", { month: "short", year: "2-digit", timeZone: "UTC" });
const MONTH_LONG = new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });

type MonthPoint = { month: string; monthLong: string; taxableValue: number | null; taxAmount: number | null; grandTotal: number | null };

function unreadable(error: unknown) {
  return <UnreadableWidgetCard title="Invoiced by month" error={error} fallback="Could not be read — this is not the same as nothing invoiced." />;
}

function MonthTip({ active, payload }: { active?: boolean; payload?: { payload: MonthPoint }[] }) {
  const point = active ? payload?.[0]?.payload : undefined;
  if (!point) return null;
  return (
    <div className="min-w-[190px] rounded-md border border-muted bg-gray-0 p-3 text-xs shadow-lg dark:bg-gray-100">
      <p className="mb-2 font-semibold text-gray-900">{point.monthLong}</p>
      <p className="flex items-center justify-between gap-4 py-0.5">
        <span className="flex items-center gap-1.5 text-gray-600">
          <span className="size-2 rounded-full" style={{ backgroundColor: STEEL }} /> Taxable value
        </span>
        <span className="tabular-nums text-gray-900">{formatMoney(point.taxableValue)}</span>
      </p>
      <p className="flex items-center justify-between gap-4 py-0.5">
        <span className="flex items-center gap-1.5 text-gray-600">
          <span className="size-2 rounded-full" style={{ backgroundColor: STEEL_LIGHT }} /> GST
        </span>
        <span className="tabular-nums text-gray-900">{formatMoney(point.taxAmount)}</span>
      </p>
      <p className="mt-1.5 flex items-center justify-between gap-4 border-t border-muted pt-1.5 font-medium">
        <span className="text-gray-600">Invoiced</span>
        <span className="tabular-nums text-gray-900">{formatMoney(point.grandTotal)}</span>
      </p>
    </div>
  );
}

function Chart({ report }: { report: SalesTrendReport }) {
  const isTablet = useMedia("(max-width: 820px)", false);
  const data: MonthPoint[] = report.buckets.map((b) => ({
    month: MONTH.format(new Date(b.periodStart)),
    monthLong: MONTH_LONG.format(new Date(b.periodStart)),
    taxableValue: money(b.taxableValue),
    taxAmount: money(b.taxAmount),
    grandTotal: money(b.grandTotal),
  }));
  const current = report.buckets.at(-1);

  return (
    <WidgetCard
      title="Invoiced by month"
      description={
        <>
          <Badge renderAsDot className="me-1" style={{ backgroundColor: STEEL }} /> Taxable value
          <Badge renderAsDot className="me-1 ms-4" style={{ backgroundColor: STEEL_LIGHT }} /> GST
        </>
      }
      descriptionClassName="text-gray-500 mt-1.5"
      headerClassName="flex-wrap gap-y-4"
      className="flex flex-col"
      action={
        <dl className="flex gap-6 text-end">
          <div>
            <dt className="text-xs text-gray-500">This month</dt>
            <dd className="font-lexend text-base font-semibold text-gray-900">{formatMoney(current ? money(current.grandTotal) : null)}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Last 12 months</dt>
            <dd className="font-lexend text-base font-semibold text-gray-900">{formatMoney(money(report.totals.grandTotal))}</dd>
          </div>
        </dl>
      }
    >
      {report.empty ? (
        <EmptyTile icon={<ChartBar />} title="No invoices in the last 12 months">
          Invoiced value appears here, month by month, once invoices are raised.
        </EmptyTile>
      ) : (
        <div className="custom-scrollbar overflow-x-auto scroll-smooth">
          <div className="h-80 w-full pt-6">
            <ResponsiveContainer width="100%" height="100%" {...(isTablet && { minWidth: "640px" })}>
              <ComposedChart data={data} barSize={24} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                <CartesianGrid vertical={false} stroke="#eceef1" className="[&_line]:stroke-gray-100" />
                <XAxis dataKey="month" axisLine={{ stroke: "#d6dae1" }} tickLine={false} tick={{ fill: "#6b7280", fontSize: 12 }} />
                <YAxis axisLine={false} tickLine={false} width={64} tick={{ fill: "#6b7280", fontSize: 12 }} tickFormatter={compactMoney} />
                <Tooltip cursor={{ fill: "rgba(42, 120, 214, 0.06)" }} content={<MonthTip />} isAnimationActive={false} />
                <Bar dataKey="taxableValue" stackId="invoiced" fill={STEEL} className="stroke-gray-0" strokeWidth={2} isAnimationActive={false} />
                <Bar dataKey="taxAmount" stackId="invoiced" fill={STEEL_LIGHT} className="stroke-gray-0" strokeWidth={2} radius={[4, 4, 0, 0]} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </WidgetCard>
  );
}

export function InvoicedByMonthTile({ resolve }: { resolve: Promise<SalesTrendReport> }) {
  return (
    <TileBoundary resolve={resolve} errorFallback={unreadable} fallbackClassName="h-[430px]">
      {(report) => <Chart report={report} />}
    </TileBoundary>
  );
}
